// ════════════════════════════════════════════════════════════════════
// i18n CATALOG — Price Tool (AdminPriceTool.js) namespace: pricetool.*
//
// One entry per user-facing STATIC UI string in AdminPriceTool.js.
// Values copied VERBATIM from the page; `ar` is accurate Modern Standard
// Arabic. The parent merges this module into lib/i18n.js afterwards.
//
// NOT included here (intentionally): dynamic data (workspace/platform/item
// names, emails), numbers, currency/figures, platform names, and brand
// names (Netrev, Stripe, Pro). Numeric-only input placeholders
// ("3.50", "9.99", "0") are left as-is per the no-numbers rule.
// ════════════════════════════════════════════════════════════════════

export const en = {
  // Section header
  "pricetool.title": "Price Tool",
  "pricetool.workspaceFallback": "Workspace",
  "pricetool.itemsSuffix": "items",
  "pricetool.allPlatforms": "All platforms",

  // Trial limit banner
  "pricetool.trial.badge": "Free trial",
  "pricetool.trial.limitReached": "You've reached your trial menu item limit.",
  "pricetool.trial.countLabelA": "of",
  "pricetool.trial.countLabelB": "menu items added",
  "pricetool.trial.upgrade": "Upgrade to add more",
  "pricetool.trial.subscribeHint": "Subscribe to track unlimited menu items and unlock all features.",
  "pricetool.trial.oneSlotLeft": "1 menu item slot left in your trial.",
  "pricetool.trial.slotsLeftSuffix": "menu item slots left in your trial.",

  // Add / edit form
  "pricetool.form.editTitle": "Edit menu item",
  "pricetool.form.newTitle": "New menu item",
  "pricetool.form.cancel": "Cancel",
  "pricetool.form.platform": "Platform",
  "pricetool.form.itemName": "Item name",
  "pricetool.form.itemNamePlaceholder": "e.g. Chicken Burger",
  "pricetool.form.cost": "Cost",
  "pricetool.form.sellingPrice": "Selling price",
  "pricetool.form.discountPct": "Discount %",
  "pricetool.form.campaignFee": "Campaign fee",
  "pricetool.form.discountCap": "Discount cap",
  "pricetool.form.capPlaceholder": "0 = none",
  "pricetool.form.saving": "Saving...",
  "pricetool.form.updateItem": "Update item",
  "pricetool.form.saveItem": "Save item",

  // Validation / action messages
  "pricetool.err.required": "Channel, name and price required",
  "pricetool.err.saveFailed": "Failed to save",
  "pricetool.confirm.delete": "Delete this item?",
  "pricetool.alert.deleteFailed": "Delete failed: ",

  // Table column headers
  "pricetool.col.channel": "Channel",
  "pricetool.col.itemName": "Item Name",
  "pricetool.col.cost": "Cost",
  "pricetool.col.sellingPrice": "Selling Price",
  "pricetool.col.discount": "Discount",
  "pricetool.col.campaignFee": "Campaign Fee",
  "pricetool.col.profit": "Profit",

  // Table cell — discount cap inline label
  "pricetool.cap": "cap",

  // Row action button tooltips
  "pricetool.action.editItem": "Edit item",
  "pricetool.action.deleteItem": "Delete item",

  // Empty state
  "pricetool.empty.title": "No menu items yet",
  "pricetool.empty.message": 'Click "+ Add menu item" above to price your first item and see its true profit.',

  // CSV import / export panel
  "pricetool.csv.title": "Import / export menu items (CSV)",
  "pricetool.csv.subtitle": "Export your items, edit in a spreadsheet, and re-import. Platform must match a platform on this workspace.",

  // CSV import row error messages (static portions; dynamic values stay raw)
  "pricetool.import.unknownPlatformPrefix": "Row",
  "pricetool.import.unknownPlatformMid": ": unknown platform",
  "pricetool.import.missingName": ": missing item name",
  "pricetool.import.missingPriceSuffix": ": missing/invalid selling price",
};

export const ar = {
  // Section header
  "pricetool.title": "أداة التسعير",
  "pricetool.workspaceFallback": "مساحة العمل",
  "pricetool.itemsSuffix": "صنف",
  "pricetool.allPlatforms": "جميع المنصّات",

  // Trial limit banner
  "pricetool.trial.badge": "النسخة التجريبية المجانية",
  "pricetool.trial.limitReached": "لقد بلغت الحد الأقصى لأصناف القائمة في النسخة التجريبية.",
  "pricetool.trial.countLabelA": "من",
  "pricetool.trial.countLabelB": "صنف مُضاف إلى القائمة",
  "pricetool.trial.upgrade": "الترقية لإضافة المزيد",
  "pricetool.trial.subscribeHint": "اشترك لتتبّع عدد غير محدود من أصناف القائمة وفتح جميع الميزات.",
  "pricetool.trial.oneSlotLeft": "تبقّى موضع واحد لأصناف القائمة في نسختك التجريبية.",
  "pricetool.trial.slotsLeftSuffix": "موضع لأصناف القائمة متبقٍّ في نسختك التجريبية.",

  // Add / edit form
  "pricetool.form.editTitle": "تعديل صنف القائمة",
  "pricetool.form.newTitle": "صنف قائمة جديد",
  "pricetool.form.cancel": "إلغاء",
  "pricetool.form.platform": "المنصّة",
  "pricetool.form.itemName": "اسم الصنف",
  "pricetool.form.itemNamePlaceholder": "مثال: برجر دجاج",
  "pricetool.form.cost": "التكلفة",
  "pricetool.form.sellingPrice": "سعر البيع",
  "pricetool.form.discountPct": "نسبة الخصم %",
  "pricetool.form.campaignFee": "رسوم الحملة",
  "pricetool.form.discountCap": "الحد الأقصى للخصم",
  "pricetool.form.capPlaceholder": "0 = بلا حد",
  "pricetool.form.saving": "جارٍ الحفظ...",
  "pricetool.form.updateItem": "تحديث الصنف",
  "pricetool.form.saveItem": "حفظ الصنف",

  // Validation / action messages
  "pricetool.err.required": "المنصّة والاسم والسعر مطلوبة",
  "pricetool.err.saveFailed": "فشل الحفظ",
  "pricetool.confirm.delete": "هل تريد حذف هذا الصنف؟",
  "pricetool.alert.deleteFailed": "فشل الحذف: ",

  // Table column headers
  "pricetool.col.channel": "القناة",
  "pricetool.col.itemName": "اسم الصنف",
  "pricetool.col.cost": "التكلفة",
  "pricetool.col.sellingPrice": "سعر البيع",
  "pricetool.col.discount": "الخصم",
  "pricetool.col.campaignFee": "رسوم الحملة",
  "pricetool.col.profit": "الربح",

  // Table cell — discount cap inline label
  "pricetool.cap": "حد",

  // Row action button tooltips
  "pricetool.action.editItem": "تعديل الصنف",
  "pricetool.action.deleteItem": "حذف الصنف",

  // Empty state
  "pricetool.empty.title": "لا توجد أصناف في القائمة بعد",
  "pricetool.empty.message": 'انقر على "+ إضافة صنف للقائمة" بالأعلى لتسعير أول صنف ومعرفة ربحه الحقيقي.',

  // CSV import / export panel
  "pricetool.csv.title": "استيراد / تصدير أصناف القائمة (CSV)",
  "pricetool.csv.subtitle": "صدّر أصنافك، وعدّلها في جدول بيانات، ثم أعد استيرادها. يجب أن تطابق المنصّة منصّةً في مساحة العمل هذه.",

  // CSV import row error messages (static portions; dynamic values stay raw)
  "pricetool.import.unknownPlatformPrefix": "الصف",
  "pricetool.import.unknownPlatformMid": ": منصّة غير معروفة",
  "pricetool.import.missingName": ": اسم الصنف مفقود",
  "pricetool.import.missingPriceSuffix": ": سعر بيع مفقود/غير صالح",
};
